import React, {ReactNode, useState} from 'react';
import styled from 'styled-components';

import {ClickEventHandler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis, flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {renderDropdownValueItems} from '../dropdowns/dropdownHelpers';
import {FilterLabel} from './common/filterLabel';
import {FilterSearchProps} from './common/types';
import {FilterDropdownBox} from './filterDropdownBox/filterDropdownBox';
import {MultiSelectFilterDropdown} from './multiSelectFilter/multiSelectFilterDropdown';

/*
 * Types.
 */

type ItemWithKey = {key: string};
type ItemRenderer<T extends ItemWithKey> = (item: T, isInsideValue: boolean) => ReactNode;

/*
 * Props.
 */

export interface SimpleFilterProps<ItemType extends ItemWithKey> {
  /** The title of the selection dropdown. */
  title: ReactNode;
  /** Placeholder value that is rendered when no items are selected */
  placeholder: ReactNode;
  items: ReadonlyArray<ItemType>;

  /** The list of selected item keys. The button will render selected items in order */
  selectedItems: ReadonlyArray<ItemType['key']>;
  setSelectedItems: HandlerOf<ReadonlyArray<ItemType['key']>>;

  renderLabel: ItemRenderer<ItemType>;
  renderIcon?: ItemRenderer<ItemType>;

  /**
   * If specified, a search header will be rendered and the items will be filtered based on the search value.
   * @param item
   */
  serializeForSearch?: (item: ItemType) => string;

  /**
   * The amount of items to render in the value button.
   */
  maxItemsToRender?: number;

  /**
   * When this is false, the filter dropdown will close after a selection,
   * and `setSelectedItems` will be called with the most recently selected item.
   *
   * @default true
   */
  isMultipleChoice?: boolean;

  onClear: ClickEventHandler;
}

/*
 * Constants.
 */

const defaultButtonMaxItemsToRender = 3;

/*
 * Styles.
 */

const StyledIconWrapper = styled.div`
  ${flexCenter};
  margin-right: 8px;
  color: ${(p) => p.theme.greys.shade70};
  grid-area: left-icon;
`;

const StyledButtonItemWrapper = styled.div`
  ${flexCenter};
  ${ellipsis()};
`;

const ButtonItemIconWrapper = styled.div`
  margin-right: 8px;
`;

/*
 * Component.
 */

export const SimpleFilterDropdown = <ItemType extends ItemWithKey>({
  title,
  placeholder,
  items,
  selectedItems,
  setSelectedItems,
  isMultipleChoice = true,
  maxItemsToRender = defaultButtonMaxItemsToRender,
  onClear,
  renderIcon,
  renderLabel,
  serializeForSearch,
}: SimpleFilterProps<ItemType>) => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const searchProps = serializeForSearch ? {searchQuery, setSearchQuery} : undefined;
  const filteredItems = filterItems(searchQuery, items, serializeForSearch);

  return (
    <FilterDropdownBox
      renderButton={() =>
        renderButton(
          placeholder,
          items.filter((i) => selectedItems.includes(i.key)),
          renderLabel,
          renderIcon,
          maxItemsToRender,
        )
      }
      renderDropdown={(popoverProps) =>
        renderDropdown(
          popoverProps,
          title,
          filteredItems,
          renderLabel,
          renderIcon,
          selectedItems,
          setSelectedItems,
          onClear,
          isMultipleChoice,
          searchProps,
        )
      }
      isActive={selectedItems.length > 0}
    />
  );
};

/*
 * Helpers.
 */

function renderButton<ItemType extends ItemWithKey>(
  placeholder: ReactNode,
  selectedItems: ReadonlyArray<ItemType>,
  renderLabel: ItemRenderer<ItemType>,
  renderIcon: ItemRenderer<ItemType> | undefined,
  maxItemsToRender: number,
) {
  if (selectedItems.length === 0) {
    return <FilterLabel>{placeholder}</FilterLabel>;
  }

  return (
    <FilterLabel>
      {renderDropdownValueItems(
        selectedItems,
        (item) => {
          const icon = renderIcon?.(item, true);

          return (
            <StyledButtonItemWrapper key={item.key}>
              {icon ? <ButtonItemIconWrapper>{icon}</ButtonItemIconWrapper> : null}
              {renderLabel(item, true)}
            </StyledButtonItemWrapper>
          );
        },
        {maxItems: maxItemsToRender},
      )}
    </FilterLabel>
  );
}

function renderDropdown<ItemType extends ItemWithKey>(
  popoverProps: PopoverTransferProps,
  title: ReactNode,
  items: ReadonlyArray<ItemType>,
  renderLabel: ItemRenderer<ItemType>,
  renderIcon: ItemRenderer<ItemType> | undefined,
  selectedItems: ReadonlyArray<ItemType['key']>,
  setSelectedItems: HandlerOf<ReadonlyArray<ItemType['key']>>,
  onClear: ClickEventHandler,
  isMultipleChoice: boolean,
  searchProps?: FilterSearchProps,
) {
  const onItemSelect = (item: ItemType) => {
    if (isMultipleChoice) {
      setSelectedItems(arrayToggle(selectedItems, item.key));
    } else if (selectedItems.length === 1 && selectedItems[0] === item.key) {
      setSelectedItems([]);
    } else {
      setSelectedItems([item.key]);
    }
  };

  const showClearButton = selectedItems.length > 0;

  return (
    <MultiSelectFilterDropdown
      {...popoverProps}
      title={title}
      searchProps={searchProps}
      rows={items.map((item) => ({
        renderer: () => renderItem(item, renderLabel, renderIcon),
        isSelected: selectedItems.includes(item.key),
        onSelect: () => onItemSelect(item),
      }))}
      showClearButton={showClearButton}
      onClear={onClear}
      isMultiSelect={isMultipleChoice}
    />
  );
}

function renderItem<ItemType extends ItemWithKey>(
  item: ItemType,
  renderLabel: ItemRenderer<ItemType>,
  renderIcon: ItemRenderer<ItemType> | undefined,
): ReactNode {
  const icon = renderIcon?.(item, false);
  return (
    <>
      {icon && <StyledIconWrapper>{renderIcon?.(item, false)}</StyledIconWrapper>}
      <DropdownItemContent>{renderLabel(item, false)}</DropdownItemContent>
    </>
  );
}

function filterItems<ItemType extends ItemWithKey>(
  searchQuery: string,
  items: ReadonlyArray<ItemType>,
  serializeForSearch?: (item: ItemType) => string,
) {
  if (!searchQuery || !serializeForSearch) {
    return items;
  }

  const lowercaseQuery = searchQuery.toLowerCase();
  return items.filter((item) => serializeForSearch(item).toLowerCase().includes(lowercaseQuery));
}
